import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import imageCompression from 'browser-image-compression';

const ImageSelectDialog = ({ open, onClose, onFileProcessed, aspectRatio = 1 }) => {
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState(aspectRatio);
  const [enableCompression, setEnableCompression] = useState(true);

  const aspectRatioOptions = [
    { label: '1:1 (Square)', value: 1, ratio: '1:1' },
    { label: '16:9 (Landscape)', value: 16/9, ratio: '16:9' },
    { label: '4:3 (Standard)', value: 4/3, ratio: '4:3' },
    { label: '3:4 (Portrait)', value: 3/4, ratio: '3:4' }
  ];

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setFileUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);

  useEffect(() => {
    if (open) {
      // Reset to default aspect ratio and compression setting when dialog opens
      setSelectedAspectRatio(aspectRatio);
      setEnableCompression(true);
    }
  }, [open, aspectRatio]);

  const onFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile.type === 'image/svg+xml') {
      onFileProcessed(selectedFile);
      handleClose();
    } else {
      setFile(selectedFile);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const onCrop = async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const image = new Image();
    image.src = fileUrl;
    image.onload = async () => {
      const { width, height } = croppedAreaPixels;
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        width,
        height,
        0,
        0,
        width,
        height
      );
      canvas.toBlob(async (blob) => {
        let finalBlob = blob;
        
        if (enableCompression) {
          finalBlob = await imageCompression(blob, { 
            maxSizeMB: 0.5, 
            maxWidthOrHeight: 1280, 
            useWebWorker: true 
          });
        }
        
        const webpFile = new File([finalBlob], 'image.webp', { type: 'image/webp' });
        onFileProcessed(webpFile);
        handleClose();
      }, 'image/webp', 1.0);
    };
  };

  const handleClose = () => {
    setFile(null);
    setFileUrl(null);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[130] bg-gray-500 bg-opacity-75 flex items-center justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-lg p-6 sm:p-8 max-w-2xl w-full max-h-[min(90dvh,100%)] overflow-y-auto">
        <h2 className="text-xl font-bold mb-6">Select Image</h2>
        <div>
          {!file ? (
            <input type="file" accept="image/*" onChange={onFileChange} />
          ) : (
            <>
              <div style={{ position: 'relative', width: '100%', height: 400 }}>
                <Cropper
                  image={fileUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={selectedAspectRatio}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  cropShape="rect"
                  showGrid={true}
                />
              </div>
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Aspect Ratio</label>
                  <div className="flex space-x-2">
                    {aspectRatioOptions.map((option) => (
                      <button
                        key={option.ratio}
                        type="button"
                        onClick={() => setSelectedAspectRatio(option.value)}
                        className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                          selectedAspectRatio === option.value
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {option.ratio}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zoom</label>
                  <input
                    type="range"
                    value={zoom}
                    min={1}
                    max={3}
                    step={0.1}
                    onChange={(e) => setZoom(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={enableCompression}
                      onChange={(e) => setEnableCompression(e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Compress image (reduces file size)
                    </span>
                  </label>
                  {enableCompression && (
                    <p className="text-xs text-gray-500 mt-1">
                      Image will be compressed to max 0.5MB and 1280px
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        <div className="flex justify-end space-x-4 mt-8">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          {file && (
            <button
              type="button"
              onClick={onCrop}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Crop
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageSelectDialog;
